import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import QuestCard from '../components/quests/QuestCard';
import api, { endpoints } from '../utils/apiClient';
import { ADMIN_ADDRESSES } from '../components/ProtectedAdminRoute';
import { FaRobot, FaPlus, FaTrophy, FaChartLine, FaGamepad, FaCoins, FaUsers } from 'react-icons/fa';
import { HiTrendingUp, HiTrendingDown } from 'react-icons/hi';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

const StatsCard = ({ title, value, icon, change, color = 'purple' }) => {
  const isPositive = change > 0;
  const colorClasses = {
    purple: 'text-purple-400',
    green: 'text-green-400',
    yellow: 'text-yellow-400',
    red: 'text-red-400'
  };

  return (
    <div className="bg-dark-300/70 backdrop-blur-lg border border-violet-500/20 rounded-xl shadow-lg p-6">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-gray-400 text-sm mb-1">{title}</h3>
          <p className="text-3xl font-bold text-white">{value}</p>
        </div>
        <div className={`p-3 rounded-lg bg-dark-600/80 ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
      <div className="flex items-center">
        <span className={`flex items-center ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
          {isPositive ? <HiTrendingUp className="mr-1" /> : <HiTrendingDown className="mr-1" />}
          {Math.abs(change)}%
        </span>
        <span className="text-gray-400 text-sm ml-1">vs last period</span>
      </div>
    </div>
  );
};

const BitPoolAIAgent = () => {
  const { user, refreshWalletBalance } = useAuth();
  const isAdmin = user?.address && ADMIN_ADDRESSES.includes(user.address.toLowerCase());
  const [quests, setQuests] = useState([]);
  const [allQuests, setAllQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({
    totalPlayers: 0,
    totalPlayersChange: 12.5,
    activeQuests: 0,
    activeQuestsChange: 8.3,
    totalRewards: 0,
    totalRewardsChange: -3.2,
    completionRate: 0,
    completionRateChange: 5.1
  });

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const questCompleted = urlParams.get('questCompleted');
  const questSuccess = urlParams.get('success') === 'true';
  const rewardAmount = urlParams.get('rewardAmount');
  const rewardCurrency = urlParams.get('rewardCurrency');

  const fetchAllData = async () => {
    try {
      console.log('Fetching data for AI Agent page...');

      // Get available quests (user hasn't played yet)
      const activeQuestsResponse = await api.get(endpoints.quests.list);
      console.log(`Received ${activeQuestsResponse.length} quests that user hasn't played`);
      setQuests(activeQuestsResponse);

      // If user is admin, also get all quests for statistics
      if (isAdmin) {
        try {
          const allQuestsResponse = await api.get(endpoints.admin.quests.list);
          setAllQuests(allQuestsResponse);

          // Calculate statistics
          calculateStats(allQuestsResponse);
        } catch (adminError) {
          console.error('Admin quest fetch error:', adminError);
        }
      } else {
        // For non-admins, use the active quests for stats (limited data)
        calculateStats(activeQuestsResponse);
      }
    } catch (error) {
      console.error('Error fetching quests:', error);
      toast.error('Failed to load quests');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (questsData) => {
    // Default values from mockup if data is insufficient
    if (!questsData || questsData.length === 0) {
      setStats({
        totalPlayers: 2547,
        totalPlayersChange: 12.5,
        activeQuests: 24,
        activeQuestsChange: 8.3,
        totalRewards: 14892,
        totalRewardsChange: -3.2,
        completionRate: 67.8,
        completionRateChange: 5.1
      });
      return;
    }

    try {
      // Calculate active quests
      const activeQuestsCount = questsData.filter(q => q.status === 'active').length;

      // Calculate total plays and completions
      let totalPlays = 0;
      let totalCompletions = 0;
      let totalRewardAmount = 0;

      questsData.forEach(quest => {
        // Add to total plays (use totalPlaysCount if available, otherwise fall back to participants count)
        totalPlays += quest.totalPlaysCount || 0;

        if (quest.participants && Array.isArray(quest.participants)) {
          // Count completions (games won > 0)
          quest.participants.forEach(participant => {
            if (participant.gamesWon && participant.gamesWon > 0) {
              totalCompletions++;
            }
          });

          // Calculate total rewards given
          if (quest.reward && quest.reward.currency === 'BAI') {
            const rewardPerPerson = Number(quest.reward.amount) || 0;
            const completions = quest.participants.filter(p => p.gamesWon && p.gamesWon > 0).length;
            totalRewardAmount += rewardPerPerson * completions;
          }
        }
      });

      // Calculate completion rate
      const completionRate = totalPlays > 0
        ? (totalCompletions / totalPlays * 100).toFixed(1)
        : 0;

      // Update stats state with real data while keeping the change percentages
      setStats(prevStats => ({
        totalPlayers: totalPlays || 2547,
        totalPlayersChange: prevStats.totalPlayersChange, // Keep the change percentage
        activeQuests: activeQuestsCount || 24,
        activeQuestsChange: prevStats.activeQuestsChange, // Keep the change percentage
        totalRewards: Math.round(totalRewardAmount) || 14892,
        totalRewardsChange: prevStats.totalRewardsChange, // Keep the change percentage
        completionRate: Number(completionRate) || 67.8,
        completionRateChange: prevStats.completionRateChange // Keep the change percentage
      }));
    } catch (error) {
      console.error('Error calculating stats:', error);
      // Use default values on error
      setStats({
        totalPlayers: 2547,
        totalPlayersChange: 12.5,
        activeQuests: 24,
        activeQuestsChange: 8.3,
        totalRewards: 14892,
        totalRewardsChange: -3.2,
        completionRate: 67.8,
        completionRateChange: 5.1
      });
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [isAdmin]);

  // Handle quest completion and reward notification
  useEffect(() => {
    if (questCompleted && user?.isConnected) {
      if (questSuccess && rewardAmount && rewardCurrency) {
        console.log("Quest completed successfully, refreshing wallet data");

        // Force multiple refreshes to ensure wallet data is updated
        const refreshInterval = setInterval(() => {
          refreshWalletBalance()
            .then((updatedUser) => {
              console.log("Wallet updated with new balances:", updatedUser);

              // Check if new balance exists in correct format
              const currencyLower = rewardCurrency.toLowerCase();
              const walletBalance = updatedUser.walletBalances?.[rewardCurrency] ||
                                   updatedUser[`${currencyLower}Balance`] || '0.00';

              console.log(`Current ${rewardCurrency} balance: ${walletBalance}`);

              toast.success(
                <div className="flex items-center gap-2">
                  <FaCoins className="text-yellow-400" />
                  <span>Received {rewardAmount} {rewardCurrency}!</span>
                </div>,
                { duration: 5000 }
              );
            })
            .catch(err => {
              console.error('Error refreshing wallet after quest completion:', err);
            });
        }, 1000); // Try every second for 5 seconds

        // Clear interval after 5 seconds
        setTimeout(() => {
          clearInterval(refreshInterval);
        }, 5000);
      } else if (questCompleted) {
        toast.error('Quest failed. Better luck next time!');
      }

      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [questCompleted, questSuccess, rewardAmount, rewardCurrency, user?.isConnected]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  const filteredQuests = activeTab === 'all'
    ? quests
    : quests.filter(quest => quest.difficulty === activeTab);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="relative">
          <div className="h-24 w-24 rounded-full border-t-4 border-l-4 border-purple-600 animate-spin"></div>
          <div className="h-24 w-24 rounded-full border-r-4 border-b-4 border-violet-500 animate-spin absolute top-0 animate-[spin_1.5s_linear_infinite]"></div>
          <div className="absolute inset-0 flex items-center justify-center text-purple-500 font-bold">
            <FaRobot className="text-2xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark py-12 relative overflow-hidden">
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-[30%] -left-[10%] w-[50%] h-[50%] bg-purple-900/10 rounded-full filter blur-[100px] animate-pulse-slow"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-violet-800/10 rounded-full filter blur-[100px] animate-pulse-slow"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Hero Section */}
        <motion.div
          className="fancy-card p-8 mb-12 relative overflow-hidden"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="absolute top-[-30%] right-[-10%] w-80 h-80 rounded-full border border-violet-500/30 opacity-50"></div>
          <div className="absolute top-[-20%] right-[-5%] w-60 h-60 rounded-full border border-violet-500/20 opacity-30"></div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2">
              <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-500 mb-4">
                BitPool AI Agent
              </h1>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl">
                Complete AI-powered quests, earn exclusive rewards, and compete in the ultimate leaderboard experience
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mb-10">
                <button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-medium py-3 px-6 rounded-lg flex items-center gap-2 shadow-lg transition-all">
                  <span className="bg-white/20 p-1 rounded">
                    <FaGamepad className="text-white" />
                  </span>
                  Start Playing
                </button>
                <button className="bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg border border-violet-500/30 transition-all">
                  Learn More
                </button>
              </div>

              {/* Feature Boxes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-dark-300/50 backdrop-blur-md rounded-xl p-5 border border-violet-500/20">
                  <div className="bg-dark-600/80 p-3 rounded-lg text-purple-400 inline-block mb-3">
                    <FaGamepad size={20} />
                  </div>
                  <h3 className="font-semibold text-white mb-2">Exciting AI Challenges</h3>
                  <p className="text-gray-400 text-sm">Compete against advanced AI agents in unique gaming experiences</p>
                </div>

                <div className="bg-dark-300/50 backdrop-blur-md rounded-xl p-5 border border-violet-500/20">
                  <div className="bg-dark-600/80 p-3 rounded-lg text-purple-400 inline-block mb-3">
                    <FaTrophy size={20} />
                  </div>
                  <h3 className="font-semibold text-white mb-2">Exclusive Rewards</h3>
                  <p className="text-gray-400 text-sm">Earn valuable tokens and unique digital collectibles</p>
                </div>

                <div className="bg-dark-300/50 backdrop-blur-md rounded-xl p-5 border border-violet-500/20">
                  <div className="bg-dark-600/80 p-3 rounded-lg text-purple-400 inline-block mb-3">
                    <FaChartLine size={20} />
                  </div>
                  <h3 className="font-semibold text-white mb-2">Competitive Leaderboard</h3>
                  <p className="text-gray-400 text-sm">Climb the ranks and showcase your gaming prowess</p>
                </div>
              </div>
            </div>

            {/* Right Column - Visual Element */}
            <div className="hidden lg:flex items-center justify-center">
              {/* This is where a circular image or design element would go */}
              <div className="w-64 h-64 rounded-full border border-violet-500/30 relative flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-500/10 to-purple-600/10"></div>
                <div className="w-56 h-56 rounded-full border border-violet-500/20 absolute"></div>
                <div className="w-48 h-48 rounded-full border border-violet-500/10 absolute"></div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <StatsCard
            title="Total Plays"
            value={stats.totalPlayers}
            icon={<FaUsers size={24} />}
            change={stats.totalPlayersChange}
          />
          <StatsCard
            title="Active Quests"
            value={stats.activeQuests}
            icon={<FaTrophy size={24} />}
            change={stats.activeQuestsChange}
          />
          <StatsCard
            title="Total Rewards"
            value={`${stats.totalRewards} BAI`}
            icon={<FaCoins size={24} />}
            change={stats.totalRewardsChange}
            color="yellow"
          />
          <StatsCard
            title="Completion Rate"
            value={`${stats.completionRate}%`}
            icon={<FaChartLine size={24} />}
            change={stats.completionRateChange}
            color="green"
          />
        </motion.div>

        {/* Admin Create Quest Link */}
        {isAdmin && (
          <motion.div
            className="fancy-card p-6 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold neon-text mb-2">Create New Quest</h2>
                <p className="text-gray-300">Design new AI challenges for players to complete</p>
              </div>
              <Link
                to="/admin/create-quest"
                className="glow-button flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <FaPlus />
                <span>Create Quest</span>
              </Link>
            </div>
          </motion.div>
        )}

        {/* Quest Filter Tabs */}
        <motion.div
          className="mb-10 flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="bg-dark-300/80 backdrop-blur-md rounded-full p-1.5 border border-violet-500/20 inline-flex">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-2 rounded-full transition-all ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-neon-violet'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              All Quests
            </button>
            <button
              onClick={() => setActiveTab('easy')}
              className={`px-6 py-2 rounded-full transition-all ${
                activeTab === 'easy'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-neon-violet'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Easy
            </button>
            <button
              onClick={() => setActiveTab('medium')}
              className={`px-6 py-2 rounded-full transition-all ${
                activeTab === 'medium'
                  ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow-neon-violet'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Medium
            </button>
            <button
              onClick={() => setActiveTab('hard')}
              className={`px-6 py-2 rounded-full transition-all ${
                activeTab === 'hard'
                  ? 'bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-neon-violet'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Hard
            </button>
          </div>
        </motion.div>

        {/* Quests Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredQuests.map((quest) => (
            <motion.div key={quest._id} variants={itemVariants}>
              <QuestCard quest={quest} />
            </motion.div>
          ))}
        </motion.div>

        {filteredQuests.length === 0 && (
          <motion.div
            className="bg-dark-300/70 backdrop-filter backdrop-blur-lg border border-violet-500/20 rounded-xl shadow-lg text-center p-12 mt-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <FaRobot className="text-5xl mx-auto mb-4 text-purple-400 opacity-50" />
            <p className="text-xl text-gray-300">
              {activeTab === 'all'
                ? "No quests available at the moment."
                : `No ${activeTab} difficulty quests available.`}
            </p>
            <p className="text-gray-400 mt-2">Check back soon for new AI challenges!</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BitPoolAIAgent;
